import { useEffect, useRef, useState } from "react";
import { useRemoteAssistSessions, remoteAssistWebSocketUrl } from "../api";
import { useRemoteAssistChannelStore } from "../store/remoteAssistStore";
import { startEmployeeSideCapture, applyInputEventOnEmployeeSide } from "../domMirror/capture";
import { SessionBanner } from "./SessionBanner";
import { SessionEndedBanner } from "./SessionEndedBanner";
import type {
  DomPatchPayload,
  InputEventPayload,
  RemoteAssistChannelMessage,
  RemoteAssistSession,
} from "../types";
import { useAuthStore } from "@/auth/authStore";

// AM-048: وقتی سشن از لیست active ناپدید می‌شود (polling)، «فقط سوکت» را تا این
// مدت باز نگه می‌داریم تا بک‌اند دلیل پایان را با close code بفرستد. بک‌اند در
// بدترین حالت با اولین پیام بعدی کلاینت (ping هر ۱۰ ثانیه + بازخوانی ۲ ثانیه‌ای
// وضعیت) کد را می‌فرستد؛ ۱۵ ثانیه از آن بیشتر است.
//
// AM-048.1 (بازبینی خصمانه): در این پنجره
//   * capture (آینه‌ی DOM) متوقف است و رویداد input_event اعمال نمی‌شود — سوکت
//     فقط شنونده‌ی close code است؛
//   * با logout یا عوض‌شدن کاربر، نگه‌داشتن همان لحظه رها می‌شود (نه بعد از ۱۵ ثانیه).
export const END_REASON_WAIT_MS = 15_000;

export function ActiveRemoteAssistOverlay() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const { data: sessions } = useRemoteAssistSessions({ enabled: status === "authenticated" });
  const { connect, disconnect, closedReason, closedRole, dismissClosedReason } =
    useRemoteAssistChannelStore();
  const [appRoot, setAppRoot] = useState<HTMLElement | null>(null);
  const [heldSession, setHeldSession] = useState<RemoteAssistSession | null>(null);
  const [previousPolled, setPreviousPolled] = useState<RemoteAssistSession | undefined>(undefined);
  const liveSessionIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    setAppRoot(document.getElementById("root") || document.body);
  }, []);

  // Find an active session where the current user is the employee
  const polledSession = sessions?.find(
    (s) => s.status === "active" && s.employee_user_id === user?.id
  );

  // سشن از لیست active ناپدید شد → آخرین سشنِ شناخته‌شده را نگه دار (فقط برای
  // زنده‌ماندن سوکت). عمداً هنگام رندر state را تنظیم می‌کنیم (نه در useEffect):
  // با useEffect یک رندرِ میانی بدونِ سشن وجود دارد و cleanup افکتِ اتصال همان
  // لحظه disconnect() را صدا می‌زد — یعنی دقیقاً همان race که این نگه‌داشتن برای
  // حذفش است.
  if (polledSession !== previousPolled) {
    setPreviousPolled(polledSession);
    if (polledSession) {
      setHeldSession(null);
    } else if (previousPolled) {
      setHeldSession(previousPolled);
    }
  }

  // نگه‌داشتن فقط برای همان کاربرِ لاگین‌شده معتبر است (logout یا سوئیچ کاربر → رها).
  const heldIsValid =
    heldSession !== null && status === "authenticated" && heldSession.employee_user_id === user?.id;

  // نگه‌داشتن تمام می‌شود وقتی دلیل رسید، مهلت گذشت، یا دیگر معتبر نیست.
  useEffect(() => {
    if (!heldSession) return;
    if (closedReason || !heldIsValid) {
      setHeldSession(null);
      return;
    }
    const timerId = setTimeout(() => setHeldSession(null), END_REASON_WAIT_MS);
    return () => clearTimeout(timerId);
  }, [heldSession, heldIsValid, closedReason]);

  // فقط سشنِ واقعاً active اجازه‌ی اعمال input_event روی صفحه‌ی کارمند را دارد.
  useEffect(() => {
    liveSessionIdRef.current = polledSession?.id;
  }, [polledSession?.id]);

  // آینه‌ی DOM: فقط تا وقتی سشن در لیست active است (نه در پنجره‌ی نگه‌داشتن).
  useEffect(() => {
    if (!polledSession || !appRoot) {
      return;
    }

    const stopCapture = startEmployeeSideCapture({
      root: appRoot,
      financialDataVisible: polledSession.financial_data_visible,
      onPatch: (payload: DomPatchPayload) => {
        useRemoteAssistChannelStore.getState().send({ type: "dom_patch", payload });
      },
    });

    return () => {
      stopCapture();
    };
  }, [polledSession?.id, appRoot, polledSession?.financial_data_visible]);

  // سوکت: تا وقتی سشن active است یا در پنجره‌ی نگه‌داشتنِ معتبر.
  const socketSession = polledSession ?? (heldIsValid ? heldSession : null);

  useEffect(() => {
    if (!socketSession || !appRoot) {
      return;
    }
    const sessionId = socketSession.id;

    connect({
      sessionId,
      role: "employee",
      url: remoteAssistWebSocketUrl(sessionId),
      onMessage: (message: RemoteAssistChannelMessage) => {
        if (message.type === "input_event" && liveSessionIdRef.current === sessionId) {
          applyInputEventOnEmployeeSide(appRoot, message.payload as InputEventPayload);
        }
      },
    });

    return () => {
      disconnect();
    };
  }, [socketSession?.id, appRoot, connect, disconnect]);

  // F2 (AM-045/AM-048): فقط برای کارمند، و فقط برای دو دلیلِ مشخص.
  if (closedRole === "employee" && (closedReason === "connection_lost" || closedReason === "revoked")) {
    return <SessionEndedBanner reason={closedReason} onDismiss={dismissClosedReason} />;
  }

  if (!polledSession) return null;

  return <SessionBanner session={polledSession} supporterDisplayName="پشتیبان سیستم" />;
}
